import {computed, EventEmitter, signal, Signal} from "@angular/core"
import {toSignal} from "@angular/core/rxjs-interop"
import type {CmLogger} from "@cm/log"
import {CM_LOGGER_TEXTURES} from "@common/services/logging"
import * as TextureEditNodes from "@app/textures/texture-editor/texture-edit-nodes"
import {
    Operator,
    OperatorCanvasToolboxType,
    OperatorFlags,
    OperatorPanelComponentType,
    OperatorProcessingHints,
    OperatorType,
} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator"
import {OperatorCallback} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator-callback"
import {TextureType} from "@generated"
import {AsyncReentrancyGuard} from "@cm/utils/async-reentrancy-guard"
import {BehaviorSubject, Subject} from "rxjs"
import {v4 as uuidv4} from "uuid"
import {ImageOpNodeBag} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/image-op-node-bag"
import {ImageOpNodeBagSet} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/image-op-node-bag-set"
import {AnyImageOpResultNode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"

export abstract class OperatorBase<NodeType extends TextureEditNodes.Operator | undefined> implements Operator {
    readonly trackId = uuidv4()

    readonly requestEvaluation = new EventEmitter<void>()

    readonly flags = new Set<OperatorFlags>()

    abstract readonly panelComponentType?: OperatorPanelComponentType
    abstract readonly canvasToolbox?: OperatorCanvasToolboxType

    abstract readonly type: OperatorType

    abstract clone(): Promise<Operator>

    protected abstract createImageOpGraph(
        sourceImage: AnyImageOpResultNode,
        hints: OperatorProcessingHints,
        imageOpNodeBag: ImageOpNodeBag,
    ): Promise<AnyImageOpResultNode>

    // abstract queueImageOps(cmdQueue: ImageOpCommandQueue, input: OperatorInput, hints: OperatorProcessingHints): Promise<OperatorOutput>

    protected readonly logger: CmLogger

    constructor(
        readonly callback: OperatorCallback,
        node: NodeType,
    ) {
        this.logger = callback.injector.get(CM_LOGGER_TEXTURES)
        this.logger.trace("Creating operator")
        this._node = node
        this._$locked.set(node?.locked ?? false)
        this._$enabled.set(node?.enabled ?? true)
        this.$valid = toSignal(this._validity$, {injector: callback.injector, initialValue: true})
    }

    async init() {
        this.logger.trace("Initializing operator")
    }

    dispose(): void {
        this.logger.trace("Disposing operator")
        this.unsubscribe.next()
        this.unsubscribe.complete()
        this._imageOpNodeBagSet.dispose()
    }

    setShouldApplyTo(textureType: TextureType, enabled: boolean) {
        if (!this.node) {
            return
        }
        if (!this.node.ignoreTextureTypes) {
            this.node.ignoreTextureTypes = []
        }
        if (enabled) {
            // remove from ignore list
            const index = this.node.ignoreTextureTypes.indexOf(textureType)
            if (index >= 0) {
                this.node.ignoreTextureTypes.splice(index, 1)
            }
        } else {
            // add to ignore list
            if (this.node.ignoreTextureTypes.indexOf(textureType) < 0) {
                this.node.ignoreTextureTypes.push(textureType)
            }
        }
        this.markEdited()
    }

    shouldApplyTo(textureType: TextureType): boolean {
        if (!this.$enabled()) {
            return false
        }
        if (this.flags.has("apply-to-all-texture-types")) {
            return true
        }
        if (textureType === TextureType.Displacement) {
            return this.shouldApplyTo(TextureType.Normal) // displacement is always mapped to whatever setting the normal map has, because it is auto generated from it
        } else {
            return !this.ignoreTextureTypes.includes(textureType)
        }
    }

    get node(): NodeType {
        return this._node
    }

    get selected(): boolean {
        return this.callback.selectedOperator === this
    }

    readonly $selected: Signal<boolean> = computed(() => this.callback.selectedOperator === this)

    setLocked(value: boolean): void {
        if (this._node && this._node.locked !== value) {
            this._node.locked = value
            this._$locked.set(value)
            this.markEdited()
        }
    }

    setEnabled(value: boolean): void {
        if (this.flags.has("no-disable") && !value) {
            this.logger.warn("Tried to disable operator that has the 'no-disable' flag")
            return
        }
        if (this._node && this._node.enabled !== value) {
            this._node.enabled = value
            this._$enabled.set(value)
            this.markEdited()
        }
    }

    private get ignoreTextureTypes(): TextureType[] {
        return this._node?.ignoreTextureTypes ?? []
    }

    requestEval(): void {
        this.requestEvaluation.emit()
    }

    markEdited(): void {
        this._$edited.set(true)
        this.callback.notifyOperatorEdited(this)
    }

    // overridden by operators that hold a CachedUpstreamValue-typed field on their node
    invalidateUpstreamCaches(): void {}

    async save(_processingJobId: string): Promise<TextureEditNodes.Operator | undefined> {
        this.resetEdited()
        return this._node
    }

    // a frame stays open across awaits and is exclusive per bag (shared bags are used by every mode and texture type),
    // so concurrent builds (e.g. preview vs. save/submit) must run one after the other
    async getImageOpGraph(sourceImage: AnyImageOpResultNode, hints: OperatorProcessingHints): Promise<AnyImageOpResultNode> {
        return this._graphBuildSerializer.executeSequentially(() => {
            const imageOpNodeBag = this._imageOpNodeBagSet.getImageOpNodeBag(hints.mode, hints.textureType)
            return imageOpNodeBag.runInFrame(() => this.createImageOpGraph(sourceImage, hints, imageOpNodeBag))
        })
    }

    protected resetEdited(): void {
        this._$edited.set(false)
    }

    protected setValid(): void {
        this._validity$.next(true)
        this._$validationMessage.set("")
    }

    protected setInvalid(message: string): void {
        this._validity$.next(false)
        this._$validationMessage.set(message)
    }

    protected unsubscribe = new Subject<void>()

    private readonly _validity$ = new BehaviorSubject<boolean>(true)
    readonly validity$ = this._validity$.asObservable()
    readonly $valid: Signal<boolean>
    private readonly _$edited = signal(false)
    readonly $edited = this._$edited.asReadonly()
    private readonly _$locked = signal(false)
    readonly $locked = this._$locked.asReadonly()
    private readonly _$enabled = signal(true)
    readonly $enabled = this._$enabled.asReadonly()
    private readonly _$validationMessage = signal("")
    readonly $validationMessage = this._$validationMessage.asReadonly()

    private _node: NodeType
    private _imageOpNodeBagSet = new ImageOpNodeBagSet()
    private readonly _graphBuildSerializer = new AsyncReentrancyGuard.PromiseSerializer()
}
